/* AgenticHome — INTENTION-FIRST + CONTEXTE ACTIF + COUCHE INSTRUMENTALE.
 * Doctrine 2026-09-09 : Ontology = monde connu · Active Context = perspective
 * de travail · Current Request = attention immédiate. Noam recommande,
 * l'utilisateur choisit. Deux densités (compacte / étendue) d'une même
 * expérience : même taxonomie (src/lib/universes.js), mêmes droits (/me),
 * mêmes recommandations, même contexte. */
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Sparkles, ArrowRight, Loader2, Mic, MicOff, Paperclip, Sun,
  Compass, FileSearch, CalendarClock, Radar, Scale, Zap, AlertTriangle, TrendingUp,
  PanelRight, PanelRightClose,
} from "lucide-react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import { useAuth } from "@/lib/auth";
import { useTranslation } from "react-i18next";
import AgenticEnvironmentGraph from "@/components/AgenticEnvironmentGraph";
import ConnectRealityPanel from "@/components/ConnectRealityPanel";
import ActiveContextBar from "@/components/ActiveContextBar";
import { getUniverses, filterUniversesByModules } from "@/lib/universes";

/* 6 familles d'intention — routées par l'Intent Router V1 existant (aucun second moteur) */
const INTENT_DEFS = [
  { icon: Compass, k: "comprendre" },
  { icon: FileSearch, k: "analyser" },
  { icon: CalendarClock, k: "preparer" },
  { icon: Radar, k: "surveiller" },
  { icon: Scale, k: "decider" },
  { icon: Zap, k: "agir" },
];

const DENSITY_KEY = "noam_agentic_density_desktop";

export default function AgenticHome() {
  const { t, i18n } = useTranslation();
  const en = (i18n.language || "fr").startsWith("en");
  const navigate = useNavigate();
  const { user } = useAuth();
  const [text, setText] = useState("");
  const [routing, setRouting] = useState(false);
  const [attention, setAttention] = useState(null);
  const [listening, setListening] = useState(false);
  const [srOk, setSrOk] = useState(false);
  const [ctx, setCtx] = useState(null);
  const [relevance, setRelevance] = useState(null);
  const [meModules, setMeModules] = useState(null);
  const [openUniverse, setOpenUniverse] = useState(null);
  // Densité mémorisée par utilisateur ET par form factor (mobile = toujours compacte)
  const [extended, setExtended] = useState(() => {
    try { return localStorage.getItem(DENSITY_KEY) === "1"; } catch { return false; }
  });
  const setDensity = (v) => {
    setExtended(v);
    try { localStorage.setItem(DENSITY_KEY, v ? "1" : "0"); } catch { /* noop */ }
  };
  const recRef = useRef(null);
  const fileRef = useRef(null);

  const firstName = (user?.name || "").trim().split(/\s+/)[0] || "";

  useEffect(() => {
    api.get("/workspace/three-q/today").then((r) => setAttention(r.data)).catch(() => {});
    api.get("/workspace/me").then((r) => setMeModules(r.data?.modules || null)).catch(() => {});
    api.post("/workspace/agentic/track", { event: "session_start_intent", meta: { surface: "agentic_home" } }).catch(() => {});
  }, []);

  // Pertinence recalculée à chaque changement de contexte (structurel + situationnel)
  useEffect(() => {
    if (!ctx) return;
    api.get(`/workspace/agentic/context/relevance?lang=${en ? "en" : "fr"}`)
      .then((r) => setRelevance(r.data)).catch(() => {});
  }, [ctx, en]);

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    setSrOk(true);
    const rec = new SR();
    rec.continuous = false;
    rec.interimResults = true;
    rec.lang = { fr: "fr-FR", en: "en-US", de: "de-DE" }[(i18n.language || "fr").slice(0, 2)] || "fr-FR";
    rec.onresult = (ev) => {
      let tx = "";
      for (let i = ev.resultIndex; i < ev.results.length; ++i) tx += ev.results[i][0].transcript;
      setText(tx);
    };
    rec.onerror = () => setListening(false);
    rec.onend = () => setListening(false);
    recRef.current = rec;
    return () => { try { rec.stop(); } catch { /* noop */ } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  const toggleMic = () => {
    const rec = recRef.current;
    if (!rec) return;
    if (listening) { rec.stop(); setListening(false); }
    else { setText(""); try { rec.start(); setListening(true); } catch { /* noop */ } }
  };

  const onFiles = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    window.__noamPendingFiles = files;
    window.__noamIntentConsumed = false;
    const q = text.trim() || t("saas.axp.analyze_default");
    navigate(`/app/intelligence?intent=${encodeURIComponent(q)}`);
    e.target.value = "";
  };

  const go = async (intent) => {
    const tx = (intent || text).trim();
    if (!tx || routing) return;
    setRouting(true);
    try {
      // Le contexte actif est un HINT de perspective (lecture seule, jamais modifié ici)
      const r = await api.post("/workspace/agentic/intent", { text: tx, context: ctx?.label || null });
      const action = (r.data.actions || [])[0];
      if (!action) { navigate("/app/chat"); return; }
      if (action.type === "navigate") navigate(action.to);
      else navigate(`/app/intelligence?intent=${encodeURIComponent(action.prefill || tx)}`);
    } catch (e) { notify.error(fmtError(e)); }
    setRouting(false);
  };

  const items = attention?.items || attention?.priorities || [];
  const universes = filterUniversesByModules(getUniverses(t, false), meModules);
  const cards = relevance?.cards || [];
  const examples = relevance?.examples || [];
  const descOf = (it) => (it.desc ? (en ? it.desc.en : it.desc.fr) : "");

  const RelevantCards = ({ compactRow }) => (
    <div data-testid="agentic-relevant">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-2.5">
        <Sparkles className="w-3 h-3 text-[#EF9F27]" /> {en ? "Relevant in this context" : "Pertinents dans ce contexte"}
      </div>
      <div className={compactRow ? "grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/5" : "space-y-px bg-white/5 border border-white/5"}>
        {cards.map((c) => (
          <button key={c.key} onClick={() => navigate(c.to)} data-testid={`agentic-relevant-${c.key}`}
            className="w-full text-left bg-[#0A0E13] px-4 py-3.5 hover:bg-white/[0.03] transition-colors">
            <div className="flex items-center gap-2 text-[13px] text-white font-medium">
              {c.label}
              <ArrowRight className="w-3 h-3 text-zinc-600 ml-auto shrink-0" />
            </div>
            <div className="text-[11px] text-zinc-500 mt-1">{c.why}</div>
          </button>
        ))}
      </div>
    </div>
  );

  const AllInstruments = ({ asList }) => (
    <div data-testid="agentic-all-instruments">
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-2.5 mt-6">
        {en ? "All your instruments" : "Tous vos instruments"}
      </div>
      {/* Rendu UNIQUE (vision Olivier) : pilules + panneau de tuiles carrées,
          en vue compacte comme en vue étendue — jamais de liste. */}
      <div className="flex flex-wrap gap-2 items-start">
        {universes.map((u) => (
          <div key={u.key} className="relative">
            <button onClick={() => setOpenUniverse(openUniverse === u.key ? null : u.key)}
              data-testid={`agentic-universe-${u.key}`}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs transition-colors ${openUniverse === u.key ? "border-[#43C6D8]/60 text-white bg-[#43C6D8]/10" : "border-white/10 bg-white/[0.02] text-zinc-400 hover:border-white/25 hover:text-white"}`}>
              <u.icon className="w-3.5 h-3.5" /> {u.label}
              <span className="text-[10px] opacity-50">{u.items.length}</span>
            </button>
          </div>
        ))}
        {!asList && (
          <button onClick={() => setDensity(true)} data-testid="agentic-density-extended"
            className="hidden lg:flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/10 text-xs text-zinc-500 hover:text-white hover:border-white/25 transition-colors">
            <PanelRight className="w-3.5 h-3.5" /> {en ? "Extended view" : "Vue étendue"}
          </button>
        )}
      </div>
      {openUniverse && (() => {
        const u = universes.find((x) => x.key === openUniverse);
        if (!u) return null;
        return (
          <div className="mt-3 border border-white/10 bg-black/30 rounded-xl overflow-hidden" data-testid="agentic-universe-panel">
            <div className="px-4 py-2.5 border-b border-white/5 flex items-center gap-2 text-[13px] text-white">
              <u.icon className="w-4 h-4 text-[#43C6D8]" /> {u.label}
              <span className="text-[10px] text-zinc-500">· {u.sub}</span>
            </div>
            <div className={`grid gap-px bg-white/5 ${asList ? "grid-cols-1 xl:grid-cols-2" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"}`}>
              {u.items.map((it) => (
                <Link key={it.to} to={it.to} data-testid={`agentic-module-${it.to.replace(/\//g, "-")}`}
                  className="bg-[#0A0E13] px-4 py-3 hover:bg-white/[0.03] transition-colors group">
                  <div className="flex items-center gap-2 text-[12px] text-white">
                    <it.icon className="w-3.5 h-3.5 text-[#43C6D8]" /> {it.label}
                    <ArrowRight className="w-3 h-3 text-zinc-700 group-hover:text-[#43C6D8] ml-auto transition-colors" />
                  </div>
                  <div className="text-[10px] text-zinc-600 mt-1">{descOf(it)}</div>
                </Link>
              ))}
            </div>
          </div>
        );
      })()}
    </div>
  );

  const mainContent = (
    <>
      {/* ─── Contexte actif + Intention ───────────────────────────────── */}
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <ActiveContextBar onChange={setCtx} />
        {extended ? (
          <button onClick={() => setDensity(false)} data-testid="agentic-density-compact"
            className="hidden lg:flex items-center gap-1.5 text-[11px] text-zinc-500 hover:text-white transition-colors">
            <PanelRightClose className="w-3.5 h-3.5" /> {en ? "Compact view" : "Vue compacte"}
          </button>
        ) : null}
      </div>
      <div className="flex items-center gap-2 mb-2">
        <Sparkles className="w-4 h-4 text-[#378ADD]" />
        <span className="noam-overline">NOAM</span>
      </div>
      <h1 className="noam-display text-3xl sm:text-4xl font-bold text-white leading-tight" data-testid="agentic-home-greeting">
        {t("saas.axp.greeting", { name: firstName ? ` ${firstName}` : "" })}
      </h1>
      <p className="text-sm text-zinc-400 mt-2">
        {t("saas.axp.greeting_sub")}
      </p>

      <div className="mt-6 max-w-3xl">
        <div className={`flex items-center gap-2 rounded-xl border bg-white/5 px-4 py-1.5 transition-colors ${listening ? "border-[#EF4444]/60" : "border-white/15 focus-within:border-[#378ADD]/60"}`}>
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && go()}
            placeholder={listening ? t("saas.axp.listening") : t("saas.axp.intent_placeholder")}
            data-testid="agentic-intent-input"
            className="flex-1 bg-transparent py-2.5 text-sm text-white outline-none placeholder:text-zinc-600"
          />
          <input ref={fileRef} type="file" multiple className="hidden" data-testid="agentic-home-file-input"
            accept=".pdf,.docx,.doc,.xlsx,.xls,.csv,.txt,.md,.png,.jpg,.jpeg" onChange={onFiles} />
          <button type="button" onClick={() => fileRef.current?.click()} data-testid="agentic-home-attach"
            title={t("saas.axp.attach")}
            className="p-1.5 rounded-full text-zinc-400 hover:text-white hover:bg-white/10 transition-colors">
            <Paperclip className="w-4 h-4" />
          </button>
          {srOk && (
            <button type="button" onClick={toggleMic} data-testid="agentic-home-mic"
              title={listening ? t("saas.axp.mic_stop") : t("saas.axp.mic_start")}
              className={`p-1.5 rounded-full transition-colors ${listening ? "bg-[#EF4444]/20 text-[#EF4444]" : "text-zinc-400 hover:text-white hover:bg-white/10"}`}>
              {listening ? <MicOff className="w-4 h-4 animate-pulse" /> : <Mic className="w-4 h-4" />}
            </button>
          )}
          <button onClick={() => go()} disabled={routing || !text.trim()}
            data-testid="agentic-intent-submit"
            className="btn-primary px-3 py-2 rounded-lg flex items-center disabled:opacity-40">
            {routing ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
          </button>
        </div>

        {/* Exemples CONTEXTUELS (la requête peut citer un objet sans changer le contexte) */}
        {examples.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2" data-testid="agentic-context-examples">
            {examples.map((e) => (
              <button key={e} onClick={() => setText(e)}
                data-testid="agentic-context-example"
                className="px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.02] text-[11px] text-zinc-400 italic hover:border-[#EF9F27]/50 hover:text-white transition-colors">
                « {e} »
              </button>
            ))}
          </div>
        )}
        <div className="mt-3 flex flex-wrap gap-2" data-testid="agentic-starters">
          {INTENT_DEFS.map((s) => (
            <button key={s.k} onClick={() => go(t(`saas.axp.iq_${s.k}`))}
              data-testid={`agentic-starter-${s.k}`}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.03] text-xs text-zinc-300 hover:border-[#378ADD]/50 hover:text-white transition-colors">
              <s.icon className="w-3.5 h-3.5 text-[#378ADD]" /> {t(`saas.axp.in_${s.k}`)}
            </button>
          ))}
        </div>

        {/* Connect Your Reality — deux chemins d'adoption, l'existant d'abord */}
        <ConnectRealityPanel />
      </div>

      {/* En compacte : pertinents + tous les instruments sous le prompt */}
      {!extended && cards.length > 0 && (
        <div className="mt-8"><RelevantCards compactRow /></div>
      )}
      {!extended && <AllInstruments asList={false} />}

      {/* ─── Aujourd'hui + un monde que NOAM comprend ──────────────────── */}
      <div className="mt-10 grid grid-cols-1 lg:grid-cols-5 gap-6 items-start">
        <div className="lg:col-span-2" data-testid="agentic-attention">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#EF9F27] font-semibold flex items-center gap-1.5">
              <Sun className="w-3 h-3" /> {t("saas.axp.today")}
            </div>
            <Link to="/app/aujourdhui" data-testid="agentic-attention-all"
              className="text-[11px] text-zinc-400 hover:text-white flex items-center gap-1 transition-colors">
              {t("saas.axp.see_all")} <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          {Array.isArray(items) && items.length > 0 ? (
            <div className="space-y-2">
              {items.slice(0, 4).map((it, i) => {
                const color = it.color || "#EF9F27";
                const Icon = it.severity === "critical" ? AlertTriangle : TrendingUp;
                return (
                  <button key={i} onClick={() => navigate(it.cta?.href || "/app/aujourdhui")}
                    data-testid={`agentic-attention-item-${i}`}
                    className="w-full text-left noam-card p-3 hover:bg-white/[0.02] transition-colors">
                    <div className="flex items-center gap-2">
                      <Icon className="w-3.5 h-3.5 shrink-0" style={{ color }} />
                      {it.module && (
                        <span className="text-[9px] uppercase tracking-widest text-zinc-500 font-semibold">{it.module}</span>
                      )}
                    </div>
                    <div className="text-[13px] text-white mt-1">{it.title || it.headline || it.label}</div>
                    {(it.why_now || it.narrative || it.why || it.subtitle) && (
                      <div className="text-[11px] text-zinc-400 mt-0.5 line-clamp-2">{it.why_now || it.narrative || it.why || it.subtitle}</div>
                    )}
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="noam-card p-5 text-center" data-testid="agentic-attention-empty">
              <p className="text-sm text-zinc-400">{t("saas.axp.no_urgency")}</p>
              <p className="text-[11px] text-zinc-600 mt-1">{t("saas.axp.no_urgency_sub")}</p>
            </div>
          )}
        </div>

        <div className="lg:col-span-3 min-h-[440px] flex flex-col">
          <AgenticEnvironmentGraph />
        </div>
      </div>
    </>
  );

  return (
    <div className="max-w-7xl mx-auto pt-8 pb-16 px-4" data-testid="agentic-home">
      {extended ? (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-8">{mainContent}</div>
          {/* Vue étendue : rail permanent — même source, mêmes droits, même contexte */}
          <div className="hidden lg:block lg:col-span-4 sticky top-4" data-testid="agentic-rail">
            {cards.length > 0 && <RelevantCards compactRow={false} />}
            <AllInstruments asList />
          </div>
          {/* Mobile : toujours compacte (form factor) */}
          <div className="lg:hidden col-span-1">
            {cards.length > 0 && <RelevantCards compactRow />}
            <AllInstruments asList={false} />
          </div>
        </div>
      ) : (
        mainContent
      )}
    </div>
  );
}
